import Web3 from 'web3';

/**
 * On-chain amount helpers. Keep human-readable token amounts as decimal **strings**
 * through to `web3.utils.toWei` — never `Number` / `parseFloat` first.
 *
 * A JS `number` is IEEE-754 (~15 significant digits). Wei amounts use 18 decimals, so
 * converting through `number` then `.toString()` can lose precision or produce scientific
 * notation that `toWei` rejects. Prefer API/form values that arrive as strings.
 */

const web3Utils = new Web3().utils;

/** Non-negative decimal string: optional fraction, no exponent, no leading `+`. */
const AMOUNT_STRING_RE = /^(?:0|[1-9]\d*)(?:\.\d+)?$/;

/**
 * Normalises an amount to a decimal string safe to pass to `toWei`.
 *
 * @throws Error when the value is missing, negative, non-finite, or not a plain decimal
 */
export function toAmountString(value: unknown): string {
    if (value === null || value === undefined || value === '') {
        throw new Error('Amount is required');
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!AMOUNT_STRING_RE.test(trimmed)) {
            throw new Error('Invalid amount: expected a non-negative decimal string');
        }
        return trimmed;
    }

    if (typeof value === 'number') {
        if (!Number.isFinite(value) || value < 0) {
            throw new Error('Invalid amount: not a finite non-negative number');
        }
        // JSON numbers may already have lost precision; still avoid "1e+21" for toWei.
        if (Number.isInteger(value) && Number.isSafeInteger(value)) {
            return String(value);
        }
        const fixed = value
            .toFixed(18)
            .replace(/(\.\d*?)0+$/, '$1')
            .replace(/\.$/, '');
        if (!AMOUNT_STRING_RE.test(fixed)) {
            throw new Error('Invalid amount after number normalisation');
        }
        return fixed;
    }

    throw new Error('Invalid amount: expected string or number');
}

/**
 * Converts a human ether/token amount to wei via `web3.utils.toWei`, after
 * {@link toAmountString}.
 */
export function toWeiEther(amount: unknown): string {
    return web3Utils.toWei(toAmountString(amount), 'ether');
}

/**
 * Truncates a decimal string for display without `parseFloat` / `Number`.
 * Pads the fractional part to `fractionDigits` (same shape as `toFixed`).
 */
export function truncateAmountString(value: string, fractionDigits: number): string {
    if (value === null || value === undefined || value === '') {
        return fractionDigits > 0 ? `0.${'0'.repeat(fractionDigits)}` : '0';
    }

    const negative = String(value).startsWith('-');
    const raw = negative ? String(value).slice(1) : String(value);
    const [wholeRaw, fracRaw = ''] = raw.split('.');
    const whole = wholeRaw.replace(/^0+(?=\d)/, '') || '0';

    if (fractionDigits <= 0) {
        return `${negative ? '-' : ''}${whole}`;
    }

    const frac = (fracRaw + '0'.repeat(fractionDigits)).slice(0, fractionDigits);
    return `${negative ? '-' : ''}${whole}.${frac}`;
}

/** True when the amount normalises to a positive (non-zero) decimal string. */
export function isPositiveAmount(value: unknown): boolean {
    try {
        const amount = toAmountString(value);
        return !/^0+(?:\.0+)?$/.test(amount);
    } catch {
        return false;
    }
}
